import React, { useEffect, useState } from 'react';
import { Bell, Loader2, CheckCheck, Crown, ShieldCheck, Radio, BookOpen, Info } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV, ADMIN_NAV } from '../../constants/nav';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../features/notifications/api/notifications';
import { enablePush, pushPermission } from '../../features/push/registerPush';
import type { AppNotification } from '../../types';
import { NOTIFICATIONS } from '../../constants/testIds';

const ICONS: Record<string, any> = {
  vip_approved: Crown, vip_rejected: Crown, vip_review: Crown, vip_request_new: Crown,
  indicator_approved: ShieldCheck, indicator_rejected: ShieldCheck, indicator_needs_revision: ShieldCheck, indicator_request_new: ShieldCheck,
  live_trade_new: Radio, course_new: BookOpen, signal_new: Radio, vip_program_new: Crown, announcement_new: Bell,
};

function relTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return 'baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} hari lalu`;
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationsPage({ admin = false }: { admin?: boolean }) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); fetchNotifications(admin).then((d) => setItems(d.items)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const readOne = async (n: AppNotification) => {
    if (n.is_read) return;
    setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    try { await markNotificationRead(n.id, admin); } catch { load(); }
  };
  const readAll = async () => {
    setBusy(true);
    try { await markAllNotificationsRead(admin); setItems((cur) => cur.map((x) => ({ ...x, is_read: true }))); toast.success('Semua notifikasi ditandai dibaca'); }
    catch (e: any) { toast.error('Gagal', { description: e.message }); }
    finally { setBusy(false); }
  };

  const unread = items.filter((i) => !i.is_read).length;

  const [pushState, setPushState] = useState<NotificationPermission | 'unsupported'>(pushPermission());
  const askPush = async () => {
    const ok = await enablePush(true);
    setPushState(pushPermission());
    if (ok) toast.success('Notifikasi HP aktif', { description: 'Anda akan menerima notifikasi walau aplikasi ditutup.' });
    else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') toast.error('Izin ditolak', { description: 'Aktifkan izin notifikasi lewat pengaturan browser Anda.' });
  };
  const showEnablePush = pushState === 'default';

  return (
    <DashboardLayout navItems={admin ? ADMIN_NAV : MEMBER_NAV} title="Notifikasi">
      <div className="max-w-2xl" data-testid={NOTIFICATIONS.page}>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><Bell className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
            <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Aktivitas</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Notifikasi{unread > 0 ? ` (${unread})` : ''}</h2></div>
          </div>
          <div className="flex items-center gap-3">
            {showEnablePush && (
              <button data-testid="enable-push-btn" onClick={askPush} className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 border border-cyan-400/30 rounded-lg px-3 py-1.5">
                <Bell className="w-4 h-4" strokeWidth={2} /> Aktifkan Notifikasi HP
              </button>
            )}
            {unread > 0 && (
              <button data-testid={NOTIFICATIONS.markAllBtn} onClick={readAll} disabled={busy} className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 disabled:opacity-50">
                <CheckCheck className="w-4 h-4" strokeWidth={2} /> Tandai semua dibaca
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : items.length === 0 ? (
          <div data-testid={NOTIFICATIONS.empty} className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
            <Bell className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.25} />
            <p className="text-white/50 text-sm">Belum ada notifikasi</p>
            <p className="text-white/30 text-xs mt-1">Update penting seputar akun Anda akan muncul di sini.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => {
              const Icon = ICONS[n.type] || Info;
              return (
                <button
                  key={n.id}
                  data-testid={NOTIFICATIONS.item}
                  onClick={() => readOne(n)}
                  className={`w-full text-left flex items-start gap-3 rounded-xl p-4 border transition-colors ${n.is_read ? 'bg-white/[0.02] border-white/10' : 'bg-cyan-400/[0.06] border-cyan-400/20'}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${n.is_read ? 'bg-white/5 text-white/50' : 'bg-cyan-400/15 text-cyan-400'}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm truncate ${n.is_read ? 'text-white/70' : 'text-white font-medium'}`}>{n.title}</p>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />}
                    </div>
                    {n.message && <p className="text-xs text-white/50 mt-0.5 line-clamp-2">{n.message}</p>}
                    <p className="text-[11px] text-white/30 mt-1">{relTime(n.created_at)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
